import type { AnyEnvelope, Envelope, RecordType } from '@wiztime/core';
import { MetaKey, type ListOptions, type ListSinceOptions, type Repo, type RepoWriter } from '../repo';
import type { OpLogEntry } from '../oplog';
import { createBackupDb, newDbInstanceId, type StoredRecord, type WizDb } from './db';

function toEnvelope(stored: StoredRecord): AnyEnvelope {
  const { pd: _pd, ...envelope } = stored;
  return envelope as AnyEnvelope;
}

function personalDateOf(record: AnyEnvelope): string | null {
  const data = record.data;
  if (record.deleted || data === null) return null;
  // The loose data schemas type unknown fields as `unknown`, so narrow by value, not by type.
  const date: unknown = 'personalDate' in data ? data.personalDate : '';
  return typeof date === 'string' ? date : '';
}

function visible<T extends RecordType>(
  stored: readonly StoredRecord[],
  options?: ListOptions,
): Array<Envelope<T>> {
  const kept = options?.includeDeleted === true ? stored : stored.filter((record) => !record.deleted);
  return kept.map(toEnvelope) as Array<Envelope<T>>;
}

/** Dexie (IndexedDB) implementation of the repository. */
export function createDexieRepo(db: WizDb): Repo {
  // A tombstone has no data, so it keeps the personal date index of the record it replaces.
  // (Synchronous on purpose: a native promise await inside a Dexie transaction would end it.)
  function pdFor(record: AnyEnvelope, existing: StoredRecord | undefined): string {
    return personalDateOf(record) ?? existing?.pd ?? '';
  }

  const writer: RepoWriter = {
    async get(id) {
      const stored = await db.records.get(id);
      return stored === undefined ? undefined : toEnvelope(stored);
    },
    async getMany(ids) {
      const stored = await db.records.bulkGet([...ids]);
      return stored.map((record) => (record === undefined ? undefined : toEnvelope(record)));
    },
    async listByType(type, options) {
      return visible(await db.records.where('type').equals(type).toArray(), options);
    },
    async listByTypeAndDate(type, date, options) {
      return visible(await db.records.where('[type+pd]').equals([type, date]).toArray(), options);
    },
    async listByTypeInDateRange(type, from, to, options) {
      const stored = await db.records
        .where('[type+pd]')
        .between([type, from], [type, to], true, true)
        .toArray();
      return visible(stored, options);
    },
    async listAll() {
      return (await db.records.toArray()).map(toEnvelope);
    },
    async listSince(hlc, options?: ListSinceOptions) {
      const index = db.records.where('hlc');
      const matching = options?.inclusive === true ? index.aboveOrEqual(hlc) : index.above(hlc);
      return (await matching.sortBy('hlc')).map(toEnvelope);
    },
    async maxHlc(notAfter) {
      const last =
        notAfter === undefined
          ? await db.records.orderBy('hlc').last()
          : await db.records.where('hlc').belowOrEqual(notAfter).last();
      return last?.hlc ?? null;
    },
    async getMeta(key) {
      return (await db.meta.get(key))?.value;
    },
    async listOpLog(query) {
      const from = query?.from ?? '';
      const to = query?.to ?? '\uffff';
      const matching = db.oplog
        .where('at')
        .between(from, to, true, true)
        .filter((entry) => query?.kind === undefined || entry.kind === query.kind);
      const all = await matching.reverse().sortBy('seq');
      return query?.limit === undefined ? all : all.slice(0, query.limit);
    },
    async countOpLog() {
      return db.oplog.count();
    },
    async appendOpLog(entry) {
      await db.oplog.add(entry as OpLogEntry);
    },
    async put(record) {
      const existing = await db.records.get(record.id);
      await db.records.put({ ...record, pd: pdFor(record, existing) });
    },
    async putMany(records) {
      const existing = await db.records.bulkGet(records.map((record) => record.id));
      await db.records.bulkPut(
        records.map((record, index) => ({ ...record, pd: pdFor(record, existing[index]) })),
      );
    },
    async setMeta(key, value) {
      await db.meta.put({ key, value });
    },
    async clearAll() {
      await db.records.clear();
      // The log holds field values (names, notes): clearing the data clears it too.
      await db.oplog.clear();
      // Whatever a sync cursor referred to is gone: the database is a new one as far as sync is concerned.
      await db.meta.put({ key: MetaKey.DbInstanceId, value: newDbInstanceId() });
    },
  };

  return {
    ...writer,
    async dbInstanceId() {
      const stored = await db.meta.get(MetaKey.DbInstanceId);
      if (stored !== undefined) return stored.value;
      // A database made before the id existed. The transaction keeps two tabs from each creating one.
      return db.transaction('rw', db.meta, async () => {
        const raced = await db.meta.get(MetaKey.DbInstanceId);
        if (raced !== undefined) return raced.value;
        const created = newDbInstanceId();
        await db.meta.put({ key: MetaKey.DbInstanceId, value: created });
        return created;
      });
    },
    // The scope function must be a native `async` function: Dexie only keeps the transaction
    // alive across the native awaits of nested async helpers when it detects one.
    transaction: (fn) => db.transaction('rw', db.records, db.meta, db.oplog, async () => fn(writer)),
    async backup() {
      const records = await db.records.toArray();
      const backup = createBackupDb(db.name);
      try {
        await backup.transaction('rw', backup.records, backup.meta, async () => {
          await backup.records.clear();
          await backup.records.bulkPut(records);
          await backup.meta.put({ key: 'createdAt', value: new Date().toISOString() });
        });
      } finally {
        backup.close();
      }
      return records.length;
    },
    async pruneOpLog(beforeIso) {
      return db.oplog.where('at').below(beforeIso).delete();
    },
  };
}
